import React from 'react';
import { motion, MotionConfig } from 'framer-motion';
import './FeaturesBento.css';

// The Features section: a bento grid of three tiles, each with one small drawn
// piece of the app instead of a screenshot. The drawings are decorative
// (aria-hidden); every tile's heading and text carry the meaning.

const Icon = ({ name }: { name: keyof typeof paths }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths[name]}
  </svg>
);

const paths = {
  camera: <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>,
  steth: <><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" /><path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" /><circle cx="20" cy="10" r="2" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  alert: <><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></>,
};

// Where the map pins go, as % of the dotted map's frame (from each city's
// latitude/longitude). Pins with a tag get a label above them.
const pins: { place: string; x: number; y: number; tag?: string; when?: string; tagOnPhone?: boolean }[] = [
  { place: 'Chicago, US', x: 20.2, y: 35.7, tag: 'Monstera', when: '2h ago', tagOnPhone: true },
  { place: 'London, UK', x: 48.8, y: 28.1, tag: 'Snake Plant', when: 'today' },
  { place: 'São Paulo, BR', x: 33.3, y: 78.1 },
  { place: 'Lagos, NG', x: 49.4, y: 60.8 },
  { place: 'Dubai, AE', x: 66.1, y: 49.2 },
  { place: 'Singapore, SG', x: 81.0, y: 62.7 },
  { place: 'Tokyo, JP', x: 93.5, y: 41.5 },
];

// Ease-out curve shared by the entrance and the health ring.
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

// Each tile rises in as it scrolls into view; tiles in the same row stagger.
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: EASE, delay },
});

const Kicker = ({ icon, children }: { icon: keyof typeof paths; children: React.ReactNode }) => (
  <p className="fb-kicker"><span className="fb-kicker-icon"><Icon name={icon} /></span>{children}</p>
);

const FeaturesBento: React.FC = () => (
  <MotionConfig reducedMotion="user">
    <div className="fb">
      {/* 1. Identify */}
      <motion.article className="fb-tile fb-id" {...reveal(0)}>
        <div className="fb-id-copy">
          <Kicker icon="camera">Instant Plant ID</Kicker>
          <h3>Identify any plant in seconds.</h3>
          <p>
            Snap a photo of any plant, flower, tree or succulent. Gardeno tells you what it is,
            how to care for it, and whether it's safe around pets.
          </p>
          <ul className="fb-ticks">
            <li>A care guide with every match</li>
            <li>Warns you if a plant is toxic to pets</li>
          </ul>
        </div>
        <div className="fb-id-art" aria-hidden="true">
          <div className="fb-viewfinder">
            <span className="fb-corner tl" /><span className="fb-corner tr" />
            <span className="fb-corner bl" /><span className="fb-corner br" />
            <span className="fb-scanline" />
            {/* Same illustration as the hero, so it is already cached. */}
            <img className="fb-plant" src="/images/snake_plant.png" alt="" loading="lazy" decoding="async" />
          </div>
          <div className="fb-match">
            <span className="fb-match-thumb"><img src="/images/snake_plant.png" alt="" loading="lazy" decoding="async" /></span>
            <div className="fb-match-name"><b>Snake Plant</b><i>Dracaena trifasciata</i></div>
            <div className="fb-match-pct">98%<small>Match</small></div>
            <div className="fb-chips">
              <span className="fb-chip warn">Toxic to pets</span>
              <span className="fb-chip sun">Low to bright light</span>
              <span className="fb-chip">Easy care</span>
            </div>
          </div>
        </div>
      </motion.article>

      {/* 2. Diagnose */}
      <motion.article className="fb-tile fb-dx" {...reveal(0.08)}>
        <Kicker icon="steth">Plant Diagnosis</Kicker>
        <h3>Find out what's wrong, and how to fix it.</h3>
        <p>Photograph a struggling plant to get a health score, the likely cause and a step-by-step treatment plan.</p>
        <div className="fb-dx-art" aria-hidden="true">
          <div className="fb-ring">
            <svg viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" className="fb-ring-track" />
              <motion.circle
                cx="60" cy="60" r="50" className="fb-ring-value"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 0.42 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: EASE, delay: 0.3 }}
              />
            </svg>
            <div className="fb-ring-label"><b>42</b><small>Health</small></div>
          </div>
          <ul className="fb-steps">
            <li className="bad"><Icon name="alert" />Root rot from overwatering</li>
            <li><Icon name="check" />Water only when the top 3 cm is dry</li>
            <li><Icon name="check" />Repot in fresh, free-draining soil</li>
          </ul>
        </div>
      </motion.article>

      {/* 3. World map */}
      <motion.article className="fb-tile fb-map" {...reveal(0)}>
        <div className="fb-map-copy">
          <Kicker icon="globe">Plant World Map</Kicker>
          <h3>See what the world is growing.</h3>
          <p>Browse plants shared by Gardeno users around the globe, and add your own finds. Always anonymous.</p>
        </div>
        <div className="fb-map-art" aria-hidden="true">
          {/* Dotted world map (generated with dotted-map); pins sit at real
              coordinates as percentages of the same 84×45 frame. */}
          <div className="fb-map-canvas">
            <img src="/images/world-dots.svg" alt="" className="fb-map-dots" />
            {pins.map((pin) => (
              <span
                key={pin.place}
                className={`fb-pin${pin.tag ? ' lg' : ''}`}
                style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              >
                {pin.tag && (
                  <span className={`fb-maptag${pin.tagOnPhone ? '' : ' wide-only'}`}>
                    {pin.tag}<i>{pin.place} · {pin.when}</i>
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      </motion.article>
    </div>
  </MotionConfig>
);

export default FeaturesBento;
